import { useEffect, useState } from "react";
import { useParams, useOutletContext, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, imgSrc } from "@/lib/api";
import Seo from "@/components/Seo";

export default function BlogPost() {
  const { postSlug } = useParams();
  const { settings, env, base } = useOutletContext();
  const [post, setPost] = useState(null);

  useEffect(() => {
    setPost(null);
    api.get(`/blog/${postSlug}`, { params: { env } }).then((r) => setPost(r.data)).catch(() => setPost(false));
  }, [postSlug, env]);

  if (post === null) return <div className="flex h-screen items-center justify-center" data-testid="post-loading"><p className="font-serif text-xl text-gold animate-pulse">Plating…</p></div>;
  if (post === false) return <div className="flex h-screen items-center justify-center text-ink2" data-testid="post-not-found">Story not found.</div>;

  return (
    <article data-testid={`blog-post-${post.slug}`}>
      <Seo
        title={`${post.title} — ${settings?.brand_name || "Chef Blaine Guillot"}`}
        siteName={settings?.brand_name || "Chef Blaine Guillot"}
        description={post.excerpt}
        image={post.image}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.excerpt,
          image: post.image,
          datePublished: post.created_at,
          author: { "@type": "Person", name: "Chef Blaine Guillot" },
        }}
      />
      <header className="relative mx-6 sm:mx-16 lg:mx-24 mt-4 flex min-h-[60vh] items-end overflow-hidden rounded-md border border-ink/10 shadow-xl">
        {post.image && <img src={imgSrc(post.image)} alt={post.title} className="absolute inset-0 h-full w-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-cast via-cast/60 to-cast/20" />
        <div className="relative z-10 mx-auto w-full max-w-4xl px-6 sm:px-12 pb-16">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs font-mono uppercase tracking-[0.3em] text-gold">
            {new Date(post.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="mt-4 text-4xl sm:text-5xl font-serif font-bold text-cream leading-tight" data-testid="post-title">
            {post.title}
          </motion.h1>
        </div>
      </header>
      <div className="mx-auto max-w-3xl px-6 sm:px-12 py-16">
        {(post.content || "").split("\n").filter(Boolean).map((para, i) => (
          <p key={i} className="mb-6 text-base sm:text-lg text-ink2 leading-relaxed first:text-xl first:text-ink2 first:font-serif first:italic">{para}</p>
        ))}
        <Link to={`${base}/blog`} data-testid="post-back-link" className="mt-8 inline-block text-xs font-semibold uppercase tracking-widest text-gold hover:text-flame transition-colors">
          ← Back to the Journal
        </Link>
      </div>
    </article>
  );
}
